/**========================================================================
 * 
 *                        GET WEEK NUMBER
 * 
 * Calculates the ISO week number for the given date (compliant to ISO 8601 definition for a week, accounts for different weekdays for 1st of January).
 * @param {Date} date_string - The date to calculate the week number for.
 * @returns {number} - The ISO week number.
 * 
 *========================================================================**/
export function get_week_number(date_string) {
  const date = new Date(date_string);
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day_num = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day_num);
  const year_start = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - year_start) / 86400000) + 1) / 7); // 86400000 = 24 * 60 * 60 * 1000
}

/**
 * Validates that a value is a valid JavaScript Date object.
 * @param {any} date - The value to validate
 * @returns {boolean} True if valid Date object, false otherwise
 */
export function validate_date(date) {
  return date instanceof Date && !isNaN(date.getTime());
}


/**========================================================================
 *                           TIMEZONE OFFSET UTILS
 *
 * Provides reliable timezone offset calculations that are:
 * - Independent of the host machine's timezone
 * - Consistent across different browsers and environments
 * - Handles daylight saving time (DST) transitions
 * - Supports all IANA timezone names and UTC offset formats
 *
 * The offset is returned in minutes with the following convention:
 * - Positive values: timezone is ahead of UTC (e.g. Asia/Tokyo: +540)
 * - Negative values: timezone is behind UTC (e.g. America/New_York: -300)
 * - Zero: timezone is UTC/GMT
 *========================================================================**/

/**
 * Calculates the UTC offset in minutes for a specific timezone and date.
 *
 * @param {string} timezone - IANA timezone identifier (e.g. 'America/New_York') or UTC offset (e.g. 'UTC+02:00')
 * @param {Date} [date=new Date()] - The date to calculate the offset for
 * @returns {number} The offset in minutes from UTC
 * @throws {Error} If the timezone is invalid or offset cannot be determined
 *
 * @example
 * get_utc_offset('Asia/Tokyo')  // Returns 540 (UTC+9)
 * get_utc_offset('America/New_York', new Date('2024-01-15'))  // Returns -300 (UTC-5)
 * get_utc_offset('America/New_York', new Date('2024-07-15'))  // Returns -240 (UTC-4)
 */
export function get_utc_offset(timezone, date = new Date()) {
  const timezone_string = typeof timezone === 'string' ? timezone.trim() : '';
  if (!timezone_string) {
    throw new Error('get_utc_offset: Missing timezone');
  }
  if (!validate_date(date)) {
    throw new Error(`get_utc_offset: Invalid date for "${timezone_string}"`);
  }

  const timezone_upper = timezone_string.toUpperCase();
  if (timezone_upper === 'UTC' || timezone_upper === 'GMT') {
    return 0;
  }

  const offset_match = timezone_string.match(/^(?:UTC|GMT)?\s*([+-])\s*(\d{1,2})(?::?(\d{2}))?$/i);
  if (offset_match) {
    const sign = offset_match[1];
    const hours = Number.parseInt(offset_match[2], 10);
    const minutes = offset_match[3] ? Number.parseInt(offset_match[3], 10) : 0;
    const total_minutes = hours * 60 + minutes;
    return sign === '-' ? -total_minutes : total_minutes;
  }

  const MILLISECONDS_PER_MINUTE = 60 * 1000;
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone_string,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  const parts = formatter.formatToParts(date);
  const parts_map = {};
  for (const part of parts) {
    if (part.type !== 'literal') {
      parts_map[part.type] = part.value;
    }
  }

  const year = Number.parseInt(parts_map.year, 10);
  const month = Number.parseInt(parts_map.month, 10);
  const day = Number.parseInt(parts_map.day, 10);
  const hour = Number.parseInt(parts_map.hour, 10);
  const minute = Number.parseInt(parts_map.minute, 10);
  const second = Number.parseInt(parts_map.second, 10);

  if (![year, month, day, hour, minute, second].every(Number.isFinite)) {
    throw new Error(`get_utc_offset: Unable to parse date parts for "${timezone_string}"`);
  }

  const utc_time = Date.UTC(year, month - 1, day, hour, minute, second);
  return Math.round((utc_time - date.getTime()) / MILLISECONDS_PER_MINUTE);
}

// EXAMPLES:
// localize_utc_date(new Date('2025-02-01T00:00:00.000+01:00'), 'Europe/Vienna').toISOString();
//   => '2025-02-01T00:00:00.000Z'
// localize_utc_date(new Date(), 'Europe/Vienna')).toISOString();
//   => '2025-01-31T14:37:00.000Z' // assuming that when called it was 2025-01-31 14:37 in CET time.
// 
// USE CASE: Assuming it is 2025-02-01 00:00 in CET, then new Date().getUTCMonth() would return 0 (Jan) instead of 1 (Feb).
// localize_utc_date(new Date(), 'Europe/Vienna')).getUTCMonth(); would correctly return 1 (Jan).
export function localize_utc_date(utc_date, timezone) {
  if (!(utc_date instanceof Date)) {
    throw new Error('Invalid date provided to localize_utc_date');
  }
  const offset_minutes = get_utc_offset(timezone, utc_date);
  const localized_date_in_utc_time = new Date(utc_date.getTime() + offset_minutes * 60 * 1000);
  return localized_date_in_utc_time;
}

/**
 * Converts a localized UTC date back to its original UTC time by removing the timezone offset
 * 
 * @param {Date} localized_utc_date - Date object previously processed by localize_utc_date()
 * @param {string} [timezone] - Timezone to unlocalize from (e.g. 'Europe/London')
 * @returns {Date} The original UTC date
 * @throws {Error} If invalid date is provided
 * 
 * @example
 * Convert CET midnight back to UTC
 * const feb_1_midnight_cet_local = localize_utc_date(new Date('2025-02-01T00:00:00.000+01:00'));
 * => '2025-02-01T00:00:00.000Z'
 * const feb_1_midnight_cet_utc = unlocalize_utc_date(feb_1_midnight_cet_local);
 * => '2025-01-31T23:00:00.000Z'
 */
export function unlocalize_utc_date(localized_utc_date, timezone) {
  if (!(localized_utc_date instanceof Date)) {
    throw new Error('Invalid date provided to unlocalize_utc_date');
  }
  const offset_minutes = get_utc_offset(timezone, localized_utc_date);
  const real_utc_time = new Date(localized_utc_date.getTime() - offset_minutes * 60 * 1000);
  return real_utc_time;
}

/**
 * Extracts the date portion (YYYY-MM-DD) from a Date object's ISO string representation
 * @param {Date} date - The date to format
 * @returns {string} Date in YYYY-MM-DD format
 */
export function yyyy_mm_dd_from_date(date) {
  if(!validate_date(date)) {
    throw new Error('Invalid date provided to yyyy_mm_dd_from_date');
  }
  return date.toISOString().split('T')[0];
}

/**
 * Formats a UTC date's time in local timezone as HH:mm
 * Uses existing localize_utc_date internally
 * @param {string|Date} utc_date - UTC date to format
 * @param {string} [timezone] - Target timezone
 * @returns {string} Time in HH:mm format
 */
export function format_utc_time_as_local(utc_date, timezone) {
  const local_date = localize_utc_date(new Date(utc_date), timezone);
  return `${local_date.getUTCHours().toString().padStart(2, '0')}:${local_date.getUTCMinutes().toString().padStart(2, '0')}`;
}

/**
 * Converts local time to UTC while preserving the local date of the reference
 * @param {string} local_time - Time in HH:mm format
 * @param {Date|string} [reference_date] - Reference date (UTC ISO string or Date)
 * @param {string} [timezone] - Source timezone (IANA identifier)
 * @returns {string} UTC ISO string
 */
export function convert_local_time_to_utc(local_time, reference_date = new Date(), timezone) {
  const base_date = new Date(reference_date);
  const [hours, minutes] = local_time.split(':');
  
  const local_date = localize_utc_date(base_date, timezone);
  local_date.setUTCHours(parseInt(hours), parseInt(minutes), 0, 0);
  
  // Use the offset at the TARGET local date/time, not the current moment
  const offset = get_utc_offset(timezone, new Date(local_date.getTime() - get_utc_offset(timezone, base_date) * 60 * 1000));
  return new Date(local_date.getTime() - offset * 60 * 1000).toISOString();
}

/**
 * Formats a UTC date in local timezone as YYYY-MM-DD
 * @param {string|Date} utc_date - UTC date to format
 * @param {string} [timezone] - Target timezone
 * @returns {string} Date in YYYY-MM-DD format
 */
export function format_utc_date_as_local(utc_date, timezone, locale) {
  // Use Intl.DateTimeFormat's formatToParts to reliably extract date parts
  const formatter = new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  const parts = formatter.formatToParts(new Date(utc_date));
  let year, month, day;
  parts.forEach(part => {
    if (part.type === 'year') {
      year = part.value;
    } else if (part.type === 'month') {
      month = part.value;
    } else if (part.type === 'day') {
      day = part.value;
    }
  });
  return `${year}-${month}-${day}`;
}

/**
 * Updates the date portion of a UTC timestamp while preserving the local time.
 * The new_date is a local date (YYYY-MM-DD in the business timezone), so we must
 * reconstruct the UTC timestamp by combining the new local date with the preserved
 * local time from the reference.
 *
 * @param {string} new_date - Local date in YYYY-MM-DD format (business timezone)
 * @param {Date|string} reference_date - UTC timestamp whose local time should be preserved
 * @param {string} [timezone] - Business timezone (IANA identifier)
 * @returns {string} New UTC ISO string with updated local date but same local time
 */
export function update_utc_date_preserve_time(new_date, reference_date, timezone) {
  if (!timezone) {
    // Fallback: preserve raw UTC components (legacy behavior for non-timezone-aware callers)
    const ref = new Date(reference_date);
    const [year, month, day] = new_date.split('-').map(Number);
    return new Date(Date.UTC(
      year, month - 1, day,
      ref.getUTCHours(), ref.getUTCMinutes(), ref.getUTCSeconds(), ref.getUTCMilliseconds()
    )).toISOString();
  }

  // Extract the local time from the reference date
  const ref_local = localize_utc_date(new Date(reference_date), timezone);
  const local_hours = ref_local.getUTCHours();
  const local_minutes = ref_local.getUTCMinutes();
  const local_seconds = ref_local.getUTCSeconds();
  const local_ms = ref_local.getUTCMilliseconds();

  // Build a UTC timestamp for midnight of the new local date
  const midnight_utc = create_utc_timestamp_that_appears_as_local_midnight(new_date, timezone);

  // Add the preserved local time
  const time_offset_ms = ((local_hours * 60 + local_minutes) * 60 + local_seconds) * 1000 + local_ms;
  return new Date(midnight_utc.getTime() + time_offset_ms).toISOString();
}

/**
 * Parses a date value as UTC.
 * If the input is a string matching the "YYYY-M(M)-D(D)" format, it returns
 * a Date object representing midnight in UTC.
 *
 * @param {string|Date} date_value - The date string or Date object.
 * @returns {Date} Date object set at midnight UTC.
 */
export function parse_date_as_utc(date_value) {
  if (typeof date_value === 'string' && /^\d{4}-\d{1,2}-\d{1,2}$/.test(date_value)) {
    const [year, month, day] = date_value.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day)); // month is 0-based
  }
  return new Date(date_value);
}

/**
 * Parses a date value as a local date in the specified timezone.
 * For a string in "YYYY-M(M)-D(D)" format, it returns a Date object that represents midnight 
 * in the given business timezone as a UTC timestamp.
 *
 * @param {string} date_value - The date string (e.g. "2025-02-24").
 * @param {string} timezone - IANA timezone string representing the business's local timezone.
 * @returns {Date} Date object adjusted so that, when formatted in the specified timezone, it displays midnight.
 */
export function create_utc_timestamp_that_appears_as_local_midnight(date_value, timezone) {
  if (typeof date_value === 'string' && /^\d{4}-\d{1,2}-\d{1,2}$/.test(date_value)) {
    const [year, month, day] = date_value.split('-').map(Number);
    // Create a candidate UTC date corresponding to the midnight of the given date.
    const candidate_utc = new Date(Date.UTC(year, month - 1, day));
    // Get the offset (in minutes) for the business timezone at the candidate date.
    const offset_minutes = get_utc_offset(timezone, candidate_utc);
    // Adjust the candidate UTC date so that it represents midnight in the business timezone.
    return new Date(candidate_utc.getTime() - offset_minutes * 60 * 1000);
  }
  return new Date(date_value);
}

/**
 * Returns the current date in the business timezone.
 * @returns {Date} Current date in business timezone.
 */
export function today_as_local(timezone) {
  return create_utc_timestamp_that_appears_as_local_midnight(yyyy_mm_dd_from_date(new Date()), timezone);
}

/**========================================================================
 * 
 *                          FORMAT DATE
 * 
 * Formats a date based on a custom format string.
 * @param {string|Date} date - The date string or Date object to format (e.g. YYYY-MM-DD or YYYY-MM-DDThh:mm:ss). Should be in UTC.
 * @param {string} parts - The date components to include using: 
 **   Y (Year), 
 **   MMMM (Month long), MMM (Month short), MM (Month 2-digit), M (Month numeric), 
 **   D (Date 2-digit) d (Date numeric), 
 **   HH (Hours 2-digit), H (Hours numeric), 
 **   m (minutes), 
 **   S (seconds), 
 **   W (week number), 
 **   EEEE (weekday long), EEE (weekday short), EE (weekday narrow).
 * @param {string} locale - The locale to use for formatting.
 * @returns {string} - The formatted date string.
 * 
 * @example format_date('2024-02-03T20:58:45', 'YDMMMMWHmsEEEE', 'de-DE')
 * Returns "2024-02-03 Samstag, KW 5 20:58:45"
 * 
 * NOTE: If you just need week number use get_week_number(date) directly
 * 
 *========================================================================**/
export function format_date(date, parts = 'DMY', locale = 'en-GB', time_zone = 'UTC', time_hour_format = '24') {

  if (!date || !(date instanceof Date || typeof date === 'string')) return date;

  if (!(date instanceof Date)) {
    date = parse_date_as_utc(date);
  }

  const options = {
    timeZone: time_zone,
    ...(parts.includes('Y') && { year: 'numeric' }),
    ...(parts.includes('M') && { month: parts.includes('MMMM') ? 'long' : parts.includes('MMM') ? 'short' : parts.includes('MM') ? '2-digit' : 'numeric' }),
    ...(parts.includes('D') && { day: '2-digit' }),
    ...(parts.includes('d') && { day: 'numeric' }),
    ...(parts.includes('H') && { hour: parts.includes('HH') ? '2-digit' : 'numeric', hour12: time_hour_format === '12' }),
    ...(parts.includes('m') && { minute: '2-digit' }),
    ...(parts.includes('S') && { second: '2-digit' }),
    ...(parts.includes('E') && { weekday: parts.includes('EEEE') ? 'long' : parts.includes('EEE') ? 'short' : 'narrow' }),
  };

  const includes_date_component = /[YMDdEW]/.test(parts);

  let formatted_date = includes_date_component
    ? date.toLocaleDateString(locale, options)
    : date.toLocaleTimeString(locale, options);

  // Custom handling for week number if 'W' is present in parts
  if (parts.includes('W')) {
    console.warn('DEPRECATED: we plan to remove the week number handling from format_date. Use get_week_number(date) directly instead.');
    const weekNum = get_week_number(date);
    // NOTE: We removed the dependency to i18n for now so we don't run into circular dependencies.
    formatted_date += `, W${weekNum}`;
  }

  return formatted_date;
}